/**
 * TesterAuthProvider — standalone lightweight tester session context.
 *
 * Story: epic-uat-portal-001-8.
 *
 * Owns the UAT-portal tester session: the in-memory token (`testerTokenStore`)
 * plus the account record returned by redeem / verify-link. Because the token
 * store is in-memory (empty on every fresh load), the provider BOOTS from it —
 * `isAuthed` is true only when a token is already present (e.g. just set
 * synchronously before navigation within the same page life). A page refresh
 * clears the token, so the tester re-authenticates via a fresh magic link.
 *
 * This is the UAT portal's OWN auth — it does NOT use `@hello-world-dao/auth`,
 * `AuthProvider`, identity-service, or any IC principal. The dao-suite pattern
 * is mirrored in shape (provider + `useTesterAuth` hook + `ProtectedRoute`), not
 * in dependency.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  getTesterToken,
  setTesterToken,
  clearTesterToken,
  subscribeToken,
  getPersistedAccount,
  setPersistedAccount,
} from './testerTokenStore';
import {
  TesterAuthContext,
  type TesterAccount,
  type TesterAuthContextValue,
} from './testerAuthContext';
import { logout as revokeTesterSession } from '@/services/testerAccountService';

export function TesterAuthProvider({ children }: { children: ReactNode }) {
  // Boot from the PERSISTED session (-15). On a fresh load / new tab, localStorage
  // may already carry a token + account, so the tester stays signed in and is
  // greeted by name without a fresh magic link.
  const [account, setAccount] = useState<TesterAccount | null>(() => getPersistedAccount());
  const [hasToken, setHasToken] = useState<boolean>(() => getTesterToken() !== null);

  // Single source of truth: resync with the store on every change — whether it
  // is cleared outside the provider (authenticatedFetch 401) or written by
  // ANOTHER tab (storage event → login/logout elsewhere). When the token goes
  // away, drop the account too so `isAuthed` can never be stale-true.
  useEffect(() => {
    return subscribeToken(() => {
      const present = getTesterToken() !== null;
      setHasToken(present);
      setAccount(present ? getPersistedAccount() : null);
    });
  }, []);

  const login = useCallback((nextAccount: TesterAccount, sessionToken: string) => {
    setTesterToken(sessionToken);
    setPersistedAccount(nextAccount);
    setAccount(nextAccount);
    setHasToken(true);
  }, []);

  const logout = useCallback(() => {
    // Revoke the server session FIRST (epic-uat-portal-001-16), while the token
    // is still present — `revokeTesterSession` attaches the current Bearer via
    // authenticatedFetch, which reads the token synchronously before clearing
    // below wipes it. Fire-and-forget: it swallows network failures, so the
    // local clear is NEVER blocked and logout always works offline.
    void revokeTesterSession();
    clearTesterToken(); // clears BOTH the persisted token and account
    setAccount(null);
    setHasToken(false);
  }, []);

  const value = useMemo<TesterAuthContextValue>(
    () => ({
      account,
      // A booted-with-token-but-no-account session (token set before the account
      // was hydrated) still counts as authed; a logged-out tester has neither.
      isAuthed: hasToken,
      login,
      logout,
    }),
    [account, hasToken, login, logout],
  );

  return (
    <TesterAuthContext.Provider value={value}>{children}</TesterAuthContext.Provider>
  );
}
